/* eslint-disable camelcase */
import { notification } from 'antd';
import { useQuery, UseQueryOptions } from 'react-query';
import { useServices } from 'services/useServices';
import { QUERY_KEYS } from 'utils/queryKeys';
import { SerializedManufacturer, SerializedMeterModel } from '@enmon/commons/lib/tools';
import { DefaultOptionType } from 'antd/lib/select';
import { useState } from 'react';
import { uniq } from 'rambda';
import { useIntl } from 'react-intl';
import { Nullable } from '@enmon/commons';
import { Commodity, isCommodity } from '@enmon/commons/lib/shared';
import { useOnlineStatus } from '../useOnlineStatus';
import { MeterModels as messages } from '../messages';

interface MeterModelsShape {
  meterModels: SerializedMeterModel[];
  isLoading: boolean;
  manufacturersOptions: DefaultOptionType[];
}

export const useMeterModels = (
  possibleCommodity?: Nullable<Commodity>,
  options: Pick<UseQueryOptions, 'staleTime' | 'cacheTime' | 'enabled'> = {},
): MeterModelsShape => {
  const [manufacturersOptions, setManufacturersOptions] = useState<DefaultOptionType[]>([]);
  const intl = useIntl();

  const {
    api: { meterModelsApiClient },
    indexedDb: { meterModelsDb },
  } = useServices();

  const { isOnline } = useOnlineStatus();

  const { data, isFetching } = useQuery({
    ...options,
    queryKey: QUERY_KEYS.meterModels.filter({ commodity: possibleCommodity ?? undefined }),
    queryFn: async ({ signal }) => {
      const meterModelsData = isOnline ? await meterModelsApiClient.getAll({ signal }) : await meterModelsDb.getData();
      const sortedModels = meterModelsData.sort((manA, manB) => manA.name?.localeCompare(manB.name ?? '') || 0) ?? [];

      await meterModelsDb.overwriteCurrentData(sortedModels);

      const commodity = isCommodity(possibleCommodity ?? '') ? possibleCommodity : null;
      const filteredMeterModels = sortedModels.filter(model => model.commodity === commodity);

      const sortedManufacturers =
        uniq(
          filteredMeterModels
            .map(model => model.manufacturer)
            .filter((manufacturer): manufacturer is SerializedManufacturer => manufacturer?.name !== undefined),
        ).sort((modelA, modelB) => modelA.name?.localeCompare(modelB.name ?? '') || 0) ?? [];

      setManufacturersOptions(
        sortedManufacturers.map(manufacturer => ({
          label: manufacturer.name,
          value: manufacturer.id,
        })),
      );

      return filteredMeterModels;
    },
    onError: error => {
      if (!isOnline) {
        notification.warning({
          message: intl.formatMessage(messages.warningMessage),
          description: intl.formatMessage(messages.warningDescription),
        });
        return;
      }

      notification.error({
        message: intl.formatMessage(messages.errorMessage),
        description: error instanceof Error ? error.message : intl.formatMessage(messages.errorDescription),
      });
    },
    // 1 minute
    cacheTime: 1000 * 60,
    enabled: Boolean(possibleCommodity),
  });

  return {
    meterModels: data ?? [],
    isLoading: isFetching,
    manufacturersOptions: manufacturersOptions ?? [],
  };
};
